/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';

const HuggingFaceSyncModal = ({ isOpen, onClose, onSuccess }) => {
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [customRepo, setCustomRepo] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [selectedVertical, setSelectedVertical] = useState('platform');
  const [syncing, setSyncing] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [result, setResult] = useState(null);

  // Vertical options with proper casing
  const verticals = [
    { value: 'wms', label: 'WMS' },
    { value: 'iseries', label: 'iSeries' },
    { value: 'civil', label: 'Civil' },
    { value: 'stitching', label: 'Stitching' },
    { value: 'platform', label: 'Platform' },
    { value: 'skillcode', label: 'SkillCode' }
  ];

  // Load repos when modal opens
  useEffect(() => {
    if (isOpen) {
      loadRepos();
      setError('');
      setResult(null);
      setStatus('');
    }
  }, [isOpen]);

  const loadRepos = async () => {
    setLoadingRepos(true);
    try {
      const response = await adminApi.get('/api/admin/adapters/hf-repos');
      const repoList = response.data?.repos || response.repos || [];
      setRepos(repoList);
      
      // If repos found, log for debugging
      console.log('[HFSyncModal] Loaded repos:', repoList);
    } catch (err) {
      console.error('Failed to load HF repos:', err);
      setRepos([]);
    } finally {
      setLoadingRepos(false);
    }
  };

  // Try to auto-detect vertical from repo name
  const detectVerticalFromRepo = (repoId) => {
    if (!repoId) return;
    
    const repoLower = repoId.toLowerCase();
    for (const v of verticals) {
      if (repoLower.includes(v.value)) {
        setSelectedVertical(v.value);
        return;
      }
    }
  };

  const handleRepoChange = (repoId) => {
    setSelectedRepo(repoId);
    detectVerticalFromRepo(repoId);
  };

  const handleCustomRepoChange = (repoId) => {
    setCustomRepo(repoId);
    detectVerticalFromRepo(repoId);
  };

  const handleSync = async () => {
    const repoId = useCustom ? customRepo : selectedRepo;

    if (!repoId) {
      setError('Please select or enter a repository');
      return;
    }

    if (!selectedVertical) {
      setError('Please select a target vertical');
      return;
    }

    setSyncing(true);
    setError('');
    setResult(null);
    setStatus('Verifying repository...');

    try {
      // Step 1: Verify repo exists
      try {
        await adminApi.get(`/api/admin/adapters/verify-hf-repo?repoId=${encodeURIComponent(repoId)}`);
      } catch (verifyErr) {
        if (verifyErr.response?.status === 404) {
          throw new Error(`Repository not found: ${repoId}. Make sure it exists and you have access.`);
        }
        throw verifyErr;
      }

      setStatus('Deploying to Modal...');

      // Step 2: Sync to Modal with vertical
      const response = await adminApi.post('/api/admin/adapters/sync-hf-to-modal', {
        repoId,
        vertical: selectedVertical,
        createAdapterRecord: true
      });

      setStatus('');
      
      // Show result
      if (response.data?.success || response.success) {
        setResult({
          success: true,
          message: response.data?.message || response.message || 'Sync completed!',
          modalPath: response.data?.modalResult?.modalPath || `/adapters/${selectedVertical}`,
          files: response.data?.modalResult?.files || []
        });
      } else {
        setResult({
          success: false,
          message: response.data?.message || response.message || 'Sync completed but deployment pending'
        });
      }

      // Call onSuccess after a brief delay to show result
      setTimeout(() => {
        onSuccess?.();
      }, 2000);

    } catch (err) {
      console.error('[HFSyncModal] Sync error:', err);
      setError(err.response?.data?.message || err.message || 'Sync failed');
      setStatus('');
    } finally {
      setSyncing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4 rounded-t-xl">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            🔄 Sync HuggingFace → Modal
          </h2>
          <p className="text-purple-100 text-sm mt-1">
            Deploy adapter from HuggingFace to Modal for inference
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Source Selection */}
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!useCustom}
                onChange={() => setUseCustom(false)}
                className="text-purple-500 focus:ring-purple-500"
              />
              <span className="text-sm font-medium">Select from list</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={useCustom}
                onChange={() => setUseCustom(true)}
                className="text-purple-500 focus:ring-purple-500"
              />
              <span className="text-sm font-medium">Enter custom repo</span>
            </label>
          </div>

          {/* Repository Selection */}
          {!useCustom ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HuggingFace Repository
              </label>
              {loadingRepos ? (
                <div className="flex items-center gap-2 text-gray-500 py-2">
                  <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                  Loading repositories...
                </div>
              ) : (
                <select
                  value={selectedRepo}
                  onChange={(e) => handleRepoChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Select a repository...</option>
                  {repos.map(repo => (
                    <option key={repo.id} value={repo.id}>
                      {repo.id} {repo.private ? '🔒' : ''}
                    </option>
                  ))}
                </select>
              )}
              {repos.length === 0 && !loadingRepos && (
                <p className="text-sm text-gray-500 mt-1">
                  No repositories found. Use "Enter custom repo" instead.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Repository ID
              </label>
              <input
                type="text"
                value={customRepo}
                onChange={(e) => handleCustomRepoChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="learnlalitratwani/platform-adapter-v1-v20260124"
              />
              <p className="text-sm text-gray-500 mt-1">
                Format: username/repo-name
              </p>
            </div>
          )}

          {/* Vertical Selection - NEW */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Vertical
            </label>
            <select
              value={selectedVertical}
              onChange={(e) => setSelectedVertical(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {verticals.map(v => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">
              Adapter will be deployed to: <code className="bg-gray-100 px-1 rounded">/adapters/{selectedVertical}/</code>
            </p>
          </div>

          {/* Status */}
          {status && (
            <div className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-200 text-purple-700 rounded-lg">
              <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
              {status}
            </div>
          )}

          {/* Success Result */}
          {result && result.success && (
            <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
              <div className="flex items-center gap-2 font-medium">
                ✅ {result.message}
              </div>
              {result.modalPath && (
                <p className="text-sm mt-1">
                  Path: <code className="bg-green-100 px-1 rounded">{result.modalPath}</code>
                </p>
              )}
              {result.files && result.files.length > 0 && (
                <p className="text-sm mt-1">
                  Files synced: {result.files.length}
                </p>
              )}
            </div>
          )}

          {/* Pending Result */}
          {result && !result.success && !error && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg">
              <div className="flex items-center gap-2">
                ⚠️ {result.message}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={syncing}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSync}
              disabled={syncing || (!selectedRepo && !customRepo)}
              className="flex-1 py-2 px-4 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing ? 'Syncing...' : 'Deploy to Modal'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HuggingFaceSyncModal;
